<template>
  <div class="branch-donut-comparison">
    <a-card
        :bordered="true"
        class="branch-donut-comparison__card pdf-avoid-break"
    >
      <a-tag class="branch-donut-comparison__tag">
        {{ previousLabel }}
      </a-tag>

      <div class="branch-donut-comparison__header">
        <div>
          <h3 class="branch-donut-comparison__title">
            {{ title }}
          </h3>

          <p
              v-if="description"
              class="branch-donut-comparison__description"
          >
            {{ description }}
          </p>
        </div>

        <QuestionCircleOutlined class="branch-donut-comparison__help-icon" />
      </div>

      <div class="branch-donut-comparison__body">
        <DomainsDonutChart
            :items="normalizedPreviousItems"
            class="branch-donut-comparison__donut"
        />

        <div class="branch-donut-comparison__legend">
          <div
              v-for="item in normalizedPreviousItems"
              :key="item.key"
              class="branch-donut-comparison__legend-row"
          >
            <span
                class="branch-donut-comparison__legend-mark"
                :style="{ backgroundColor: item.color }"
            ></span>

            <span class="branch-donut-comparison__legend-text">
              {{ item.label }} — {{ item.value }} чел.
            </span>
          </div>
        </div>
      </div>
    </a-card>

    <a-card
        :bordered="true"
        class="branch-donut-comparison__card pdf-avoid-break"
    >
      <a-tag class="branch-donut-comparison__tag branch-donut-comparison__tag--current">
        {{ currentLabel }}
      </a-tag>

      <div class="branch-donut-comparison__header">
        <div>
          <h3 class="branch-donut-comparison__title">
            {{ title }}
          </h3>

          <p
              v-if="description"
              class="branch-donut-comparison__description"
          >
            {{ description }}
          </p>
        </div>

        <QuestionCircleOutlined class="branch-donut-comparison__help-icon" />
      </div>

      <div class="branch-donut-comparison__body">
        <DomainsDonutChart
            :items="normalizedCurrentItems"
            class="branch-donut-comparison__donut"
        />

        <div class="branch-donut-comparison__legend">
          <div
              v-for="item in normalizedCurrentItems"
              :key="item.key"
              class="branch-donut-comparison__legend-row"
          >
            <span
                class="branch-donut-comparison__legend-mark"
                :style="{ backgroundColor: item.color }"
            ></span>

            <span class="branch-donut-comparison__legend-text">
              {{ item.label }} — {{ item.value }} чел.
            </span>
          </div>
        </div>
      </div>
    </a-card>
  </div>
</template>

<script>
import { computed } from 'vue'
import { QuestionCircleOutlined } from '@ant-design/icons-vue'

import DomainsDonutChart from '@/components/Analytics/DomainsDonutChart.vue'

const FALLBACK_COLORS = [
  '#6C5CE7',
  '#A29BFE',
  '#00B5AD',
  '#71E5E0',
  '#D5DBE2',
]

export default {
  name: 'BranchDonutComparisonBlock',

  components: {
    QuestionCircleOutlined,
    DomainsDonutChart,
  },

  props: {
    previousLabel: {
      type: String,
      default: 'Было',
    },

    currentLabel: {
      type: String,
      default: 'Стало',
    },

    title: {
      type: String,
      default: '',
    },

    description: {
      type: String,
      default: '',
    },

    previousItems: {
      type: Array,
      default: () => [],
    },

    currentItems: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const normalizedPreviousItems = computed(() => {
      return normalizeItems(props.previousItems)
    })

    const normalizedCurrentItems = computed(() => {
      return normalizeItems(props.currentItems)
    })

    return {
      normalizedPreviousItems,
      normalizedCurrentItems,
    }
  },
}

function normalizeItems(items) {
  return (Array.isArray(items) ? items : []).map((item, index) => ({
    ...item,
    key: item?.key || `item-${index}`,
    label: item?.label || '',
    value: toNumber(item?.value),
    color: item?.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length],
  }))
}

function toNumber(value) {
  const number = Number(value)

  return Number.isFinite(number) ? number : 0
}
</script>

<style scoped lang="scss">
.branch-donut-comparison {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.branch-donut-comparison__card {
  position: relative;
  min-width: 0;
  border-radius: 8px;
}

.branch-donut-comparison__card :deep(.ant-card-body) {
  padding: 18px 16px 16px;
}

.branch-donut-comparison__tag {
  position: absolute;
  top: -10px;
  left: 20px;
  z-index: 2;
  margin: 0;
  height: 20px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  font-size: 12px;
  line-height: 18px;
  background: #ffffff;
  color: rgba(0, 0, 0, 0.65);
  border-color: #d5dbe2;
}

.branch-donut-comparison__tag--current {
  color: var(--geek-blue);
  border-color: var(--geek-blue-3);
  background-color: var(--geek-blue-1);
}

.branch-donut-comparison__header {
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}

.branch-donut-comparison__title {
  margin: 0 0 8px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.branch-donut-comparison__description {
  margin: 0;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.45);
}

.branch-donut-comparison__help-icon {
  flex: 0 0 auto;
  margin-top: 2px;
  color: rgba(0, 0, 0, 0.25);
  font-size: 14px;
}

.branch-donut-comparison__body {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 20px;
}

.branch-donut-comparison__donut {
  width: 128px;
  height: 112px;
  flex: 0 0 128px;
}

.branch-donut-comparison__legend {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.branch-donut-comparison__legend-row {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.branch-donut-comparison__legend-mark {
  width: 10px;
  height: 10px;
  flex: 0 0 auto;
  border-radius: 2px;
}

.branch-donut-comparison__legend-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: $tablet-breakpoint) {
  .branch-donut-comparison {
    grid-template-columns: 1fr;
  }
}

@media (max-width: $mobile-breakpoint) {
  .branch-donut-comparison__card :deep(.ant-card-body) {
    padding: 18px 12px 14px;
  }

  .branch-donut-comparison__body {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }

  .branch-donut-comparison__donut {
    width: 140px;
    height: 120px;
    flex-basis: 120px;
  }
}
</style>